import React, { useMemo } from "react";

import { useTracking } from "@local-logic/core/tracking";
import { useTranslation } from "react-i18next";

import {
  TrainIcon,
  PencilRulerIcon,
  ShoppingCartIcon,
  ConfettiIcon,
  PlantIcon,
  FlowerLotusIcon,
} from "@phosphor-icons/react";

import {
  type CategoryKey,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/LocalContent/types";
import { ItemButton } from "~/sdks/LocalContent/components/ItemButton";

import { useRootElement } from "../context";

const categoryIcons: Record<CategoryKey, React.ElementType> = {
  transport: TrainIcon,
  education: PencilRulerIcon,
  amenities: ShoppingCartIcon,
  character: ConfettiIcon,
  nature: FlowerLotusIcon,
  wellness: PlantIcon,
};

export function Category({
  categoryKey,
  backgroundColor,
  fillColor,
}: {
  categoryKey: CategoryKey;
  backgroundColor?: string;
  fillColor?: string;
}) {
  const { t } = useTranslation("localContent");
  const tracking = useTracking();
  const { onSelectCategory } = useRootElement();

  const Icon = useMemo(() => categoryIcons[categoryKey], [categoryKey]);

  const handleOnSelectCategory = (category: CategoryKey | null) => () => {
    if (typeof onSelectCategory === "undefined") {
      return;
    }

    onSelectCategory(category)();

    const eventAttributes = {
      category: categoryKey,
    };

    tracking.postEvent({
      eventType: TrackingPipelineEvents.CATEGORIES_MENU_CATEGORY_SELECTED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.CATEGORIES_MENU_CATEGORY_SELECTED,
      eventAttributes,
    });
  };

  return (
    <ItemButton
      data-testid="categories-toggle-category"
      data-category={categoryKey}
      text={t(`categories.${categoryKey}`)}
      onClick={handleOnSelectCategory(categoryKey)}
      isDisabled={false}
      icon={{
        element: <Icon />,
        backgroundColor,
        fillColor,
      }}
    />
  );
}
